import MomentUtils from '@date-io/moment';
import { makeStyles, Theme } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import classNames from 'classnames';
import { Moment } from 'moment';
import React, { useEffect } from 'react';

import formatDate from '../../utils/formatDate';

const FONT_REG = {
  fontFamily: 'Avenir, Arial, Verdana, sans-serif',
  fontWeight: 400,
};

interface Props {
  readonly name: string;
  readonly setOuterState?: (value: string) => void;
  readonly controlledValue?: string;
  readonly error?: string;
  readonly touched?: boolean;
  readonly onChange: (value: string) => void;
  readonly onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly value: string;
  readonly label: string;
  readonly placeholder?: string;
  readonly minDate?: string;
  readonly maxDate?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly monthYearOnly?: boolean;
}

export default function DatePicker(props: Readonly<Props>) {
  const { touched, onChange, required, setOuterState, error, value, ...propsToPass } = props;
  const classes = useStyles();

  useEffect(() => {
    if (value) {
      const formattedDate = formatDate(value);
      onChange(formattedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (date: Moment | null) => {
    if (!date) {
      onChange('');

      return;
    }

    const formattedDate = date.format('MM/DD/YYYY');
    if (setOuterState) {
      setOuterState(formattedDate);
    }
    onChange(formattedDate);
  };

  const isError = () => {
    if ((touched && error) || (touched === undefined && error)) {
      return true;
    }

    return false;
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <KeyboardDatePicker
        error={isError()}
        required={required}
        helperText={isError() ? error : null}
        FormHelperTextProps={{ className: classes.helperText }}
        variant="inline"
        value={value || null}
        {...propsToPass}
        onChange={(date: MaterialUiPickersDate) => handleDateChange(date as Moment | null)}
        fullWidth
        InputProps={{
          className: classNames(props.disabled ? classes.disabledInput : classes.input, {
            [classes.errorInput]: isError(),
          }),
          inputProps: {
            className: classes.innerInput,
            placeholder: props.placeholder,
          },
          disableUnderline: true,
          autoComplete: 'off',
        }}
        InputLabelProps={{
          className: props.disabled ? classes.disabledLabel : classes.label,
          shrink: !!props.placeholder || !!value,
        }}
        style={{
          marginBottom: isError() ? 0 : '22px',
        }}
        KeyboardButtonProps={{
          classes: {
            disabled: classes.disabled,
          },
        }}
        autoOk
        data-testid={props.label}
        views={props.monthYearOnly ? ['year', 'month'] : undefined}
        format={props.monthYearOnly ? 'MM/YYYY' : 'MM/DD/YYYY'}
      />
    </MuiPickersUtilsProvider>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  disabled: {
    color: '#1C1E20 !important',
  },
  disabledLabel: {
    color: '#1C1E20 !important',
    transform: 'none !important',
    fontSize: 16,
    padding: 16,
    zIndex: 10000,
  },
  input: {
    fontSize: 16,
    color: theme.palette.text.primary,
    fontFamily: FONT_REG.fontFamily,
    paddingLeft: 16,
    marginTop: '0 !important',
    marginBottom: 0,
    height: 80,
    backgroundColor: 'transparent',
    width: '100%',
    borderWidth: '1px',
    borderColor: theme.palette.divider,
    borderStyle: 'solid',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    borderRadius: 4,
  },
  disabledInput: {
    fontSize: 16,
    color: theme.palette.text.primary + ' !important',
    fontFamily: FONT_REG.fontFamily,
    paddingLeft: 16,
    marginTop: '0 !important',
    marginBottom: 0,
    height: 80,
    backgroundColor: theme.palette.action.disabledBackground,
    width: '100%',
    borderWidth: '1px',
    borderColor: theme.palette.divider,
    borderStyle: 'solid',
    borderRadius: 4,
  },
  innerInput: {
    padding: '28px 0px 13px',
  },
  label: {
    transform: 'none !important',
    fontSize: 16,
    padding: 16,
    '& .MuiFormLabel-asterisk': {
      color: 'red',
    },
  },
  helperText: {
    marginLeft: 14,
    marginRight: 14,
  },
  errorInput: {
    borderColor: 'red',
  },
}));
